import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import "@/App.css";

import PipelineNodes from "@/components/PipelineNodes";
import AgentCard from "@/components/AgentCard";
import FinalPromptCard from "@/components/FinalPromptCard";
import HistorySidebar from "@/components/HistorySidebar";
import BriefComposer from "@/components/BriefComposer";
import { FORGE } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_b0f0f9b1-18c4-4169-9d57-74f35eb2af07/artifacts/z5a1xd9x_PLAD_GOLD_png.webp";

function Header({ config, llmLabel }) {
    return (
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[color:var(--border-subtle)]">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
                <div className="flex items-center gap-4">
                    <img
                        data-testid={FORGE.logo}
                        src={LOGO_URL}
                        alt="Profecia Links"
                        className="h-11 w-auto object-contain"
                    />
                    <div className="gold-divider self-stretch" />
                    <div>
                        <h1 className="font-serif text-2xl leading-none text-[color:var(--text-main)]">
                            PromptForge
                        </h1>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)] mt-1">
                            by Profecia Links
                        </div>
                    </div>
                </div>
                <div className="hidden md:block ml-auto text-right">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">
                        Multi-Agent Prompt Refinement Engine
                    </div>
                    <div className="text-xs text-[color:var(--text-main)] font-light mt-1">
                        {config
                            ? `${config.agents.length} agents · driven by ${llmLabel}`
                            : "loading pipeline…"}
                    </div>
                </div>
            </div>
        </header>
    );
}

function Footer() {
    return (
        <footer className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 mt-10 border-t border-[color:var(--border-subtle)] flex flex-col md:flex-row justify-between gap-3 text-xs text-[color:var(--text-muted)] font-light">
            <div>
                © {new Date().getFullYear()} Profecia Links · Dubai · Dublin · Pune
            </div>
            <div className="font-mono uppercase tracking-widest text-[10px]">
                PromptForge · Higgsfield-ready prompt orchestration
            </div>
        </footer>
    );
}

function App() {
    const [config, setConfig] = useState(null);
    const [brief, setBrief] = useState("");
    const [llmKey, setLlmKey] = useState("claude-sonnet-4-5");
    const [brandIdx, setBrandIdx] = useState(0);
    const [contentIdx, setContentIdx] = useState(0);
    const [aspectIdx, setAspectIdx] = useState(0);

    const [running, setRunning] = useState(false);
    const [statuses, setStatuses] = useState({}); // {agent_id: "idle"|"active"|"done"}
    const [outputs, setOutputs] = useState({}); // {agent_id: string}
    const [activeIndex, setActiveIndex] = useState(-1);
    const [error, setError] = useState(null);
    const [files, setFiles] = useState([]); // uploaded reference material

    const [history, setHistory] = useState([]);
    const [activeRunId, setActiveRunId] = useState(null);

    const abortRef = useRef(null);
    const pollRef = useRef(null);

    // Bootstrap config + history
    useEffect(() => {
        axios.get(`${API}/config`).then((res) => {
            setConfig(res.data);
            setBrief(res.data.demo_brief || "");
            const s = {};
            res.data.agents.forEach((a) => (s[a.id] = "idle"));
            setStatuses(s);
        });
        loadHistory();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    const loadHistory = async () => {
        try {
            const res = await axios.get(`${API}/pipeline/history`);
            setHistory(res.data);
        } catch (e) {
            console.error("history load", e);
        }
    };

    const llmLabel = useMemo(() => {
        if (!config) return "";
        const o = config.llm_options.find((x) => x.key === llmKey);
        return o ? o.label : llmKey;
    }, [config, llmKey]);

    const resetPipeline = () => {
        if (!config) return;
        const s = {};
        config.agents.forEach((a) => (s[a.id] = "idle"));
        setStatuses(s);
        setOutputs({});
        setActiveIndex(-1);
        setError(null);
    };

    const handleRun = useCallback(async () => {
        if (!config || !brief.trim() || running) return;
        resetPipeline();
        setRunning(true);
        setActiveRunId(null);

        try {
            const res = await axios.post(`${API}/pipeline/run`, {
                brief: brief.trim(),
                brand_index: brandIdx,
                content_type_index: contentIdx,
                aspect_index: aspectIdx,
                llm_key: llmKey,
                file_ids: files.map((f) => f.id),
            });
            const { run_id } = res.data;
            setActiveRunId(run_id);
            startPolling(run_id);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Pipeline failed";
            setError(msg);
            setRunning(false);
        }
    }, [brief, brandIdx, contentIdx, aspectIdx, llmKey, config, running, files]);

    const startPolling = (runId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const poll = async () => {
            try {
                const res = await axios.get(`${API}/pipeline/status/${runId}`);
                applyState(res.data);
                if (["completed", "failed", "cancelled"].includes(res.data.status)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setRunning(false);
                    setActiveIndex(-1);
                    if (res.data.status === "failed" && res.data.error) {
                        setError(res.data.error);
                    }
                    loadHistory();
                }
            } catch (e) {
                console.error("poll", e);
            }
        };
        // Poll fast for a streaming-like UX
        pollRef.current = setInterval(poll, 700);
        poll();
    };

    const applyState = (state) => {
        if (!state) return;
        setStatuses(state.agent_states || {});
        setOutputs(state.outputs || {});
        if (state.active_agent && config) {
            const idx = config.agents.findIndex((a) => a.id === state.active_agent);
            setActiveIndex(idx);
        } else {
            setActiveIndex(-1);
        }
    };

    const handleStop = async () => {
        if (activeRunId) {
            try {
                await axios.post(`${API}/pipeline/cancel/${activeRunId}`);
            } catch (e) {
                console.error(e);
            }
        }
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        setRunning(false);
        setActiveIndex(-1);
    };

    const handleLoadDemo = () => {
        if (config) setBrief(config.demo_brief || "");
    };

    const handleSelectHistory = async (id) => {
        try {
            const res = await axios.get(`${API}/pipeline/history/${id}`);
            const doc = res.data;
            setActiveRunId(id);
            setBrief(doc.brief);
            // Try to restore selectors
            if (config) {
                const bi = config.brands.findIndex((b) => b.name === doc.brand);
                if (bi >= 0) setBrandIdx(bi);
                const ci = config.content_types.indexOf(doc.content_type);
                if (ci >= 0) setContentIdx(ci);
                const ai = config.aspect_ratios.indexOf(doc.aspect_ratio);
                if (ai >= 0) setAspectIdx(ai);
            }
            setOutputs(doc.outputs || {});
            const s = {};
            (config?.agents || []).forEach((a) => {
                s[a.id] = doc.outputs && doc.outputs[a.id] ? "done" : "idle";
            });
            setStatuses(s);
            setError(null);
        } catch (e) {
            console.error(e);
        }
    };

    const handleNewRun = () => {
        setActiveRunId(null);
        resetPipeline();
        if (config) setBrief(config.demo_brief || "");
    };

    const finalOutput = outputs.assembler || "";

    return (
        <div className="App grain-bg min-h-screen">
            <Header config={config} llmLabel={llmLabel} />

            {/* Sticky pipeline visualization */}
            <div className="sticky top-[80px] z-30 bg-[color:var(--app-bg)]/85 backdrop-blur-xl border-b border-[color:var(--border-subtle)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-5 overflow-x-auto">
                    {config && (
                        <PipelineNodes
                            agents={config.agents}
                            statuses={statuses}
                            activeIndex={activeIndex}
                        />
                    )}
                </div>
            </div>

            <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
                    <div className="flex-1 min-w-0 space-y-8">
                        <BriefComposer
                            brief={brief}
                            setBrief={setBrief}
                            llmKey={llmKey}
                            setLlmKey={setLlmKey}
                            brandIdx={brandIdx}
                            setBrandIdx={setBrandIdx}
                            contentIdx={contentIdx}
                            setContentIdx={setContentIdx}
                            aspectIdx={aspectIdx}
                            setAspectIdx={setAspectIdx}
                            files={files}
                            setFiles={setFiles}
                            config={config}
                            running={running}
                            onRun={handleRun}
                            onStop={handleStop}
                            onLoadDemo={handleLoadDemo}
                            error={error}
                        />

                        {config && (
                            <section className="space-y-4">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--profecia-gold)] font-mono">
                                            Agent Outputs
                                        </div>
                                        <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--text-main)] leading-none mt-1">
                                            Pipeline Transcript
                                        </h2>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {config.agents.map((agent, idx) => (
                                        <AgentCard
                                            key={agent.id}
                                            agent={agent}
                                            index={idx}
                                            status={statuses[agent.id] || "idle"}
                                            output={outputs[agent.id] || ""}
                                            defaultOpen={idx === 0}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        <FinalPromptCard
                            finalOutput={finalOutput}
                            running={running}
                        />
                    </div>

                    <HistorySidebar
                        history={history}
                        activeRunId={activeRunId}
                        onSelect={handleSelectHistory}
                        onNewRun={handleNewRun}
                    />
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default App;
